import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Header } from './Header';
import { Footer } from './Footer';
import { Contact } from './Contact';
import { Phone, MapPin, CheckCircle, Snowflake, Gauge, Wind, Droplets, ThermometerSun, Gift } from 'lucide-react';

export const AcService = () => {
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Car AC Repair Dubai | AC Gas Refill Starting AED 179 | Think Mechanic</title>
        <meta name="description" content="Car AC not cooling? Get expert car AC repair & gas refill in Dubai starting AED 179. Fast service, leak test & diagnostics. Book now & get 20% OFF." />
        <link rel="canonical" href="https://thinkmechanicdxb.com/car-ac-expert" />
        <meta name="keywords" content="car AC repair Dubai, car AC gas refill Dubai, car AC not cooling Dubai, auto AC repair near me, car AC service Dubai, car AC fixing Dubai, AC gas refill price Dubai, car air conditioning repair Dubai, car AC specialist Dubai, car AC check Dubai, car AC gas refill Dubai price, AC repair Dubai cost, car AC service price UAE, cheap car AC repair Dubai, AC gas refill R134a Dubai, car AC inspection Dubai, BMW AC repair Dubai, Mercedes AC repair Dubai, Audi AC repair Dubai, Volkswagen AC repair Dubai, German car AC repair Dubai, European car AC service Dubai, car AC repair Ras Al Khor, car AC repair Al Quoz, car AC repair Dubai near me, car AC service Sharjah, auto AC repair Dubai industrial area" />
      </Helmet>

      <Header />

      {/* Hero Section */}
      <section 
        className="relative min-h-[65vh] sm:min-h-[60vh] flex items-center justify-center bg-cover bg-center pt-20 sm:pt-0"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0.75)), url('https://images.unsplash.com/photo-1487754180451-c456f719a1fc?q=80&w=2000')`,
        }}
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            {/* Service Badge - Better positioned for mobile */}
            <div className="inline-flex items-center space-x-2 bg-red-600/20 border border-red-600 px-3 sm:px-6 py-1.5 sm:py-3 rounded-full mb-4 sm:mb-6">
              <Snowflake className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
              <span className="text-red-600 font-semibold text-xs sm:text-base">Professional AC Service</span>
            </div>

            <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-3 sm:mb-6 leading-tight px-2">
              Car AC Not Cooling?
              <span className="text-red-600"> Get Expert Repair in Dubai</span>
            </h1>

            <p className="text-base sm:text-xl lg:text-2xl text-gray-300 mb-6 sm:mb-8 px-2">
              Car AC Repair & Gas Refill Dubai | Starting AED 179
            </p>

            {/* Pricing Cards */}
            <div className="grid md:grid-cols-2 gap-4 sm:gap-6 max-w-3xl mx-auto mb-6 sm:mb-8 px-2">
              {/* Japanese/Korean/American */}
              <div className="bg-gradient-to-br from-zinc-900 to-black border-2 border-red-600 rounded-2xl p-5 sm:p-6">
                <div className="text-center">
                  <p className="text-gray-300 text-xs sm:text-sm mb-2">Starting from</p>
                  <p className="text-4xl sm:text-5xl font-black text-red-600 mb-2 sm:mb-3">AED 179</p>
                  <p className="text-white font-semibold text-base sm:text-lg">
                    Japanese / Korean / American
                  </p>
                </div>
              </div>

              {/* German/European */}
              <div className="bg-gradient-to-br from-zinc-900 to-black border-2 border-red-600 rounded-2xl p-5 sm:p-6">
                <div className="text-center">
                  <p className="text-gray-300 text-xs sm:text-sm mb-2">Starting from</p>
                  <p className="text-4xl sm:text-5xl font-black text-red-600 mb-2 sm:mb-3">AED 279</p>
                  <p className="text-white font-semibold text-base sm:text-lg">
                    German / European
                  </p>
                </div>
              </div>
            </div>

            {/* Quick Contact */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:+971566671468"
                className="inline-flex items-center justify-center bg-red-600 hover:bg-red-700 text-gray-900 px-8 py-4 rounded-lg text-lg font-bold transition-all duration-200 shadow-lg hover:shadow-red-600/50"
              >
                <Phone className="mr-2 w-5 h-5" />
                Call: 056 667 1468
              </a>
              <a
                href="https://wa.me/971566671468?text=Hi%20Think%20Mechanic%2C%20I%20want%20to%20book%20a%20Car%20AC%20service%20and%20claim%20the%2020%25%20offer%20(Website)."
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center bg-green-600 hover:bg-green-700 text-gray-900 px-8 py-4 rounded-lg text-lg font-bold transition-all duration-200 shadow-lg"
              >
                <svg className="mr-2 w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                WhatsApp Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Website Exclusive Offer - Highlighted */}
      <section className="py-8 sm:py-12 bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-400 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, #000000 1px, transparent 0)',
            backgroundSize: '30px 30px'
          }}></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            {/* Exclusive Badge */}
            <div className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm border-2 border-black px-4 sm:px-6 py-1.5 sm:py-2 rounded-full mb-3 sm:mb-4 animate-pulse">
              <Gift className="w-4 h-4 sm:w-5 sm:h-5 text-black" />
              <span className="text-black font-black text-xs sm:text-sm lg:text-base uppercase tracking-wider">
                🌐 Website Exclusive Offer
              </span>
            </div>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-black text-black mb-3 sm:mb-4 drop-shadow-lg">
              GET 20% OFF
            </h2>
            
            <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-black mb-2 sm:mb-3">
              On Your Total Bill
            </p>
            
            <div className="inline-block bg-white text-yellow-400 px-6 sm:px-8 py-3 sm:py-4 rounded-xl shadow-2xl">
              <p className="text-base sm:text-lg lg:text-xl font-bold">
                ✨ When You Book Via Our Website ✨
              </p>
            </div>

            <p className="text-black/80 font-semibold mt-3 sm:mt-4 text-xs sm:text-sm lg:text-base px-4">
              Valid only for online bookings through thinkmechanicdxb.com
            </p>
          </div>
        </div>
      </section>

      {/* Services Included */}
      <section className="py-16 bg-gradient-to-b from-black to-zinc-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 text-center mb-4">
              What's <span className="text-red-600">Included</span>
            </h2>
            <p className="text-gray-600 text-center mb-12 text-lg">
              Professional AC service - not cheap, but complete!
            </p>

            <div className="grid sm:grid-cols-2 gap-6">
              {/* Service Item 1 */}
              <div className="bg-white border border-gray-200 rounded-xl p-6 hover:border-red-600 transition-all">
                <div className="flex items-start space-x-4">
                  <div className="bg-red-600/20 p-3 rounded-lg flex-shrink-0">
                    <ThermometerSun className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">AC Cooling Test</h3>
                    <p className="text-gray-600">Complete system cooling performance evaluation</p>
                  </div>
                </div>
              </div>

              {/* Service Item 2 */}
              <div className="bg-white border border-gray-200 rounded-xl p-6 hover:border-red-600 transition-all">
                <div className="flex items-start space-x-4">
                  <div className="bg-red-600/20 p-3 rounded-lg flex-shrink-0">
                    <Gauge className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Gas Pressure Check</h3>
                    <p className="text-gray-600">Precise refrigerant pressure measurement</p>
                  </div>
                </div>
              </div>

              {/* Service Item 3 */}
              <div className="bg-white border border-gray-200 rounded-xl p-6 hover:border-red-600 transition-all">
                <div className="flex items-start space-x-4">
                  <div className="bg-red-600/20 p-3 rounded-lg flex-shrink-0">
                    <Droplets className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Basic Leak Inspection</h3>
                    <p className="text-gray-600">Visual inspection for common leak points</p>
                  </div>
                </div>
              </div>

              {/* Service Item 4 */}
              <div className="bg-white border border-gray-200 rounded-xl p-6 hover:border-red-600 transition-all">
                <div className="flex items-start space-x-4">
                  <div className="bg-red-600/20 p-3 rounded-lg flex-shrink-0">
                    <Snowflake className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Gas Refill (R134a)</h3>
                    <p className="text-gray-600">Premium quality refrigerant refill</p>
                  </div>
                </div>
              </div>

              {/* Service Item 5 */}
              <div className="bg-white border border-gray-200 rounded-xl p-6 hover:border-red-600 transition-all sm:col-span-2">
                <div className="flex items-start space-x-4">
                  <div className="bg-red-600/20 p-3 rounded-lg flex-shrink-0">
                    <Wind className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Vent Temperature Test</h3>
                    <p className="text-gray-600">Final verification of optimal cooling performance</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Options */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 text-center mb-12">
              Service <span className="text-red-600">Options</span>
            </h2>

            {/* Best Option - Recommended */}
            <div className="bg-gradient-to-br from-red-600 to-red-700 rounded-2xl p-8 mb-8 border-4 border-yellow-400 relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-yellow-400 text-black px-6 py-2 rounded-full font-black text-sm">
                  ⭐ BEST OPTION (RECOMMENDED)
                </span>
              </div>
              
              <div className="mt-4">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Workshop Visit</h3>
                
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-gray-900 font-semibold text-lg">Workshop Service (Ras Al Khor)</p>
                      <p className="text-white/80">Professional facility with all equipment</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-gray-900 font-semibold text-lg">Pickup & Delivery Available</p>
                      <p className="text-white/80">
                        <span className="font-bold">Free</span> for nearby areas • 
                        <span className="font-bold"> Paid</span> for far areas
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Service Areas */}
            <div className="bg-zinc-800 rounded-xl p-6 text-center">
              <MapPin className="w-8 h-8 text-red-600 mx-auto mb-3" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Serving All Areas in Dubai & Sharjah
              </h3>
              <p className="text-gray-600">
                Ras Al Khor Workshop • Pickup & Delivery Services Available
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 text-center mb-4">
              Why Choose <span className="text-red-600">Think Mechanic</span>
            </h2>
            <p className="text-gray-600 text-center mb-12 text-lg">
              Professional AC service you can trust
            </p>

            <div className="grid sm:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="bg-red-600/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Expert Technicians</h3>
                <p className="text-gray-600">Certified AC specialists</p>
              </div>

              <div className="text-center">
                <div className="bg-red-600/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Snowflake className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Quality Parts</h3>
                <p className="text-gray-600">Genuine R134a refrigerant</p>
              </div>

              <div className="text-center">
                <div className="bg-red-600/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Phone className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Fast Service</h3>
                <p className="text-gray-600">Same-day service available</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <Contact />

      <Footer />
    </div>
  );
};
